import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Close, Copy } from "../../assets/icons";
import ChipsDropdown, { OptionsProps } from "../common/chips-dropdown";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { useParams } from "react-router-dom";
import useProjectFacade from "../../facade/project.Facade";
import Loader from "../common/loader";
import { showToast } from "../../utilities/commonUtilities";
import useCalculationFacade from "../../facade/calculation/calculation.facade";
import { ProjectStatus } from "../../utilities/AppConstants";
import { Tab4Input } from "../../store/model/calculations/projectTab";
import { User } from "../../store/dashboard/AuthSlice";
import AuthFacade from "../../facade/auth.Facade";
import SelectCreate from "../common/select-create";
import useProjectHeaderFacade from "../../facade/calculation/calculation-input/projectHeader.facade";
import { CasesModel } from "../../models/ShareWithCustomerModel";

const ShareWithCustomer: React.FC<{ show: boolean, close?: (show: boolean) => void }> = ({
    show = false,
    close = () => { },
}) => {
    const { triggerShareWithCustomer, triggerUpdateProjectStatus, triggerGetSharedCases } = useProjectFacade();
    const { getProjectAndAffiliationsDetails } = useProjectHeaderFacade();
    const { updateProjectStatus } = useCalculationFacade();
    const { getAllCustomers } = AuthFacade();
    const [loading, setLoading] = useState<boolean>(false);
    const tab4Input: Tab4Input | null = useAppSelector(
        (state) => state?.projectTab?.tab4Input
    );

    const customers: User[] | undefined = useAppSelector(
        (state) => state?.auth?.customers
    );
    const { user } = useAppSelector((state) => state?.auth);
    const { projectAndAffiliations } = useAppSelector(
        (state) => state?.projectHeader
    );
    const { customer } = useAppSelector((state) => state?.createProject);
    const { sharedCases } = useAppSelector((state) => state?.myProjects);
    const { ID, caseID } = useParams();
    const caseId = caseID?.split("caseID=")?.[1] || "";
    const parentProjectID = ID?.split("projectID=")?.[1];

    const shareUrl = `${window.location.origin}/project-details/projectID=${parentProjectID}`;
    const [error, setError] = useState('');
    const [emails, setEmails] = useState('');
    const [receiverName, setReceiverName] = useState('');
    const [receiverDisplayName, setReceiverDisplayName] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState([] as { id?: string, label?: string, firstName?: string }[]);
    const [filteredCases, setFilteredCases] = useState([] as { value?: string, name?: string }[]);
    const [selectedUserEmail, setSelectedUserEmail] = useState('');

    const [selectedOption, setSelectedOption] = useState([{ value: parentProjectID as string, editable: false }] as OptionsProps[]);

    const mapCustomersToOptions = (customersList: User[] | undefined) => {
        if (!customersList) return [];
        
        const uniqueCustomers = customersList.filter((user, index, self) =>
            index === self.findIndex((u) => u.email === user.email)
        );
        
        // sort ascending by full name (firstName + lastName)
        uniqueCustomers.sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });
        
        return uniqueCustomers.map((x: User, index: number) => ({ 
            id: x.email || `user-${index}`,
            label: (`${x.firstName || ''}${x.firstName && x.lastName ? ' ' : ''}${x.lastName || ''}`).trim() || x.email || `user-${index}`, 
            name: x.firstName || ''
        }));
    };

    useEffect(() => {
        if (sharedCases?.length) {
            setEmails(sharedCases?.[0]?.email || '');
            setReceiverName(sharedCases?.[0]?.email || '');
            setSelectedOption(sharedCases?.[0]?.case?.map((sharedCase: any) => ({ value: sharedCase, editable: sharedCase === parentProjectID ? false : true })) || []);
        } else {
            setSelectedOption([{ value: parentProjectID as string, name: projectAndAffiliations?.find((item: any) => item?.projectID === parentProjectID)?.caseName, editable: false }] as OptionsProps[])
            setEmails('');
            setReceiverName('');
            setMsg('');
        }
    }, [sharedCases])

    const [msg, setMsg] = useState('');

    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => alert("Copied to clipboard!"))
            .catch(err => console.error("Failed to copy:", err));
    };

    const shareWithCustomer = async () => {
        setLoading(true);
        let cases: CasesModel[] = [];
        if (selectedOption?.length) {
            selectedOption.forEach((option: any) => cases.push({ projectId: option.value, caseName: option.name }));
        }
        const response = await triggerShareWithCustomer({
            copyLink: shareUrl,
            email: emails,
            senderName: user?.firstName,
            projectName: tab4Input?.projectName,
            receiverName,
            msg,
            selectedCases: cases,
            parentId: parentProjectID,
            wellName: tab4Input?.wellName ? String(tab4Input?.wellName) : ''
        });
        if (response?.meta?.requestStatus === 'fulfilled') {
            const statusChangeResponse = await triggerUpdateProjectStatus({ id: (caseId || parentProjectID) as string, status: ProjectStatus.needReview, updateTree: true });
            setLoading(false);
            if (statusChangeResponse.payload === 'Success') {
                showToast('The cases have been successfully shared with the customer');
                parentProjectID && triggerGetSharedCases(parentProjectID);
                await getProjectAndAffiliationsDetails(parentProjectID ?? '')
                setTimeout(() => updateProjectStatus(ProjectStatus.needReview), 2000);
            } else {
                showToast('An error occurred while sharing the cases with the customer.', 'error');
            }
        } else {
            showToast('An error occurred while sharing the cases with the customer.', 'error');
        }

        close(true);
    }

    const checkError = (value: string) => {
        value = value || '';
        let errorMessage = "";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value.trim().length < 1) {
            errorMessage = "Share via email is required";
        } else if (!emailRegex.test(value.trim())) {
            errorMessage = "Please enter valid Share via email";
        }
        setError(errorMessage);
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            if (customer) {
                setLoading(true);
                await getAllCustomers(customer as string);
                setLoading(false);
            }
        };

        fetchCustomers();
    }, [customer]);

    useEffect(() => {
        setFilteredCustomers(mapCustomersToOptions(customers));
        setFilteredCases(projectAndAffiliations?.map((x: any) => ({ value: x.projectID, name: x.caseName })) || []);
    }, [customers, projectAndAffiliations]);

    const handleOnChangeSelect = (
        option: { [key: string]: any },
        name?: string | undefined
    ) => {
        if (option?.target?.value) {
            setEmails(option?.target?.value);
            setSelectedUserEmail('');
            setReceiverName('User');
            checkError(option?.target?.value);
        } else {
            setEmails(option.id);
            setReceiverName(option.name);
            setReceiverDisplayName(option.label);
            setSelectedUserEmail(option.id);
            checkError(option.id);
        }
        setFilteredCustomers(mapCustomersToOptions(customers));
    }

    const onChangeSearchCustomer = async (e: KeyboardEvent) => {
        const { value } = e.target as HTMLInputElement;
        const keyword = value?.trim() || '';
        const filtered = customers?.filter((user: User) =>
            ["email", "firstName", "lastName"].some(key =>
                (user[key as keyof User] as string)?.toLowerCase().includes(keyword.toLowerCase())
            )
        ) || [];
        
        setFilteredCustomers(mapCustomersToOptions(filtered));
    };

    const handleClearCustomer = () => {
        setEmails('');
        setSelectedUserEmail('');
        checkError('');
    }
    const isValid = (!error && emails?.trim() !== '' && selectedOption?.length > 0);

    const handleClose = () => {
        if (sharedCases?.length) {
            setSelectedOption(sharedCases?.[0]?.case?.map((sharedCase: any) => ({ value: sharedCase, editable: sharedCase === parentProjectID ? false : true })) || []);
        } else {
            setSelectedOption([{ value: parentProjectID as string, editable: false }] as OptionsProps[])
        }
        setEmails(sharedCases?.[0]?.email || '');
        setReceiverName(sharedCases?.[0]?.email || '');
        setMsg('');
        setError('');
        setSelectedUserEmail('');
        close(false)
    }

    const handleOnChangeCases = (option: any) => {
        setSelectedOption(option);
    }

    return (
        <Dialog open={show} onClose={() => close(false)} className="relative z-[1002]">
            <DialogBackdrop
                transition
                className="fixed inset-0 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-[calc(100vw-50px)]">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="flex dialog-max-height relative transform overflow-hidden rounded-lg border-[2px] border-darkcolor-400 bg-darkcolor-700 text-neutral-25 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm md:min-w-[700px] md:w-[700px]  data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <Loader isLoading={loading} />
                        <div className="flex flex-col w-full ">
                            <div className="border-b-[2px] border-darkcolor-400 py-5 px-6">
                                <div className="flex flex-row justify-between text-neutral-25">
                                    <div>
                                        <div className="text-[19px] font-semibold">Share ‘{tab4Input?.projectName}’</div>
                                    </div>
                                    <a href="javascript:void(0)" onClick={() => handleClose()}>
                                        <Close />
                                    </a>
                                </div>
                            </div>
                            <div className="w-full p-6 flex flex-col gap-4">
                                <div className="flex flex-col w-full gap-2">
                                    <div className="flex items-center">
                                        <SelectCreate
                                            options={filteredCustomers}
                                            onChange={(option, name) => handleOnChangeSelect(option, name)}
                                            padding="px-[14px] sm:py-[10px] py-[5px]"
                                            fontSize="16px"
                                            className="sm:px-0 px-[10px] w-full"
                                            selectedValue={filteredCustomers?.length > 0 ? emails : receiverDisplayName}
                                            textInput={false}
                                            placeholder="Select an customer"
                                            required={true}
                                            label="Share via email"
                                            name="customer"
                                            targetName="customer"
                                            showCloseIcon={!!selectedUserEmail}
                                            isSearch={true}
                                            onChangeSearch={onChangeSearchCustomer}
                                            onClear={handleClearCustomer}
                                            onCloseDropdown={() => setFilteredCustomers(mapCustomersToOptions(customers))}
                                        />
                                    </div>
                                    {(emails !== sharedCases?.[0]?.email && sharedCases?.length > 0) && (
                                        <div className="text-error-700 text-sm mt-1 w-full">
                                            Access for the existing user will be revoked after changing the email.
                                        </div>
                                    )}
                                    {error && (
                                        <div className="text-error-700 text-sm mt-1 w-full">
                                            {error}
                                        </div>
                                    )}
                                </div>
                                <div className="flex w-full flex-col">
                                    <label className="text-neutral-25 font-medium mr-2 mb-1">
                                        Select Cases
                                    </label>
                                    <ChipsDropdown
                                        options={filteredCases}
                                        onSelect={(options) => handleOnChangeCases(options)}
                                        selectedOptions={selectedOption}
                                        disabled={false}
                                    />
                                </div>
                                <div className="flex flex-col w-full gap-2">
                                    <label className="text-neutral-25 font-medium mr-2">
                                        Message
                                    </label>
                                    <div className="flex items-center">
                                        <textarea
                                            placeholder="Write here"
                                            name="notes"
                                            value={msg}
                                            onChange={(e) => setMsg(e.target.value)}
                                            className={`p-[8px] w-full border-[1px] rounded-[8px] border-darkcolor-400 focus:outline-none bg-darkcolor-700 text-neutral-25`}
                                            rows={4}
                                            maxLength={300}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col w-full gap-2">
                                    <label className="text-neutral-25 font-medium mr-2">
                                        Copy link
                                    </label>
                                    <div className="flex items-center relative">
                                        <input
                                            type="text"
                                            disabled={true}
                                            value={shareUrl}
                                            name={"productionRate"}
                                            className="pr-10 w-full opacity-70 overflow-hidden text-ellipsis whitespace-nowrap h-12 px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                                        />
                                        <span className="absolute right-[10px]" onClick={copyToClipboard}>
                                            <Copy />
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="sticky bottom-0 bg-darkcolor-700 border-t-[1px] border-t-darkcolor-400 px-6 py-4 flex justify-end space-x-4 z-[10011]">
                                <button
                                    onClick={() => handleClose()}
                                    className="flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] border-darkcolor-400 text-sm font-normal text-neutral-25 bg-darkcolor-600 hover:bg-darkcolor-500 hover:border-darkcolor-300 transition"
                                >
                                    Cancel
                                </button>
                                <button disabled={!isValid} onClick={shareWithCustomer}
                                    className={`flex items-center justify-center py-2 px-4 gap-2 rounded-[8px] border-[1px] text-sm font-bold shadow-custom ${!isValid ? 'border-[1px] bg-darkcolor-700 border-darkcolor-700 text-darkcolor-300 cursor-not-allowed' : 'text-white border-rgba(255, 255, 255, 0.12) bg-primary-500 hover:bg-primary-600 active:text-[#FCFCFD]'}`}>Send</button>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>

    );
}

export default ShareWithCustomer;
